import type { ArenaSnapshot, MarketUpdate, Round } from '@pons/contracts';
// A baseline started before a successful POST must not erase its acknowledged receipt.
export function retainAcceptedPredictions(
  snapshot: ArenaSnapshot,
  accepted: ReadonlyMap<string, Round>,
): ArenaSnapshot {
  const attach = (round: Round): Round => {
    const receipt = accepted.get(round.id);
    return receipt
      ? {
          ...round,
          choice: receipt.choice,
          chosenAt: receipt.chosenAt,
          lockedOdds: receipt.lockedOdds,
        }
      : round;
  };
  const predictions = snapshot.predictions.map(attach);
  const ids = new Set(predictions.map((r) => r.id));
  for (const receipt of accepted.values()) {
    if (!ids.has(receipt.id))
      predictions.push(
        attach(snapshot.rounds.find((r) => r.id === receipt.id) || receipt),
      );
  }
  return { ...snapshot, rounds: snapshot.rounds.map(attach), predictions };
}
export function mergeMarket(
  snapshot: ArenaSnapshot,
  update: MarketUpdate,
): ArenaSnapshot {
  const publicRows = new Map(
    update.rounds.map((round) => {
      const {
        choice: _choice,
        chosenAt: _chosenAt,
        votes: _votes,
        survivesVotes: _survivesVotes,
        lockedOdds: _lockedOdds,
        ...fields
      } = round;
      return [round.id, { ...fields, split: null } as Round];
    }),
  );
  function merge(round: Round, patch: Round): Round {
    return {
      ...patch,
      ...(round.choice
        ? {
            choice: round.choice,
            chosenAt: round.chosenAt,
            lockedOdds: round.lockedOdds,
            votes: round.votes,
            survivesVotes: round.survivesVotes,
            split: round.split,
          }
        : {}),
    };
  }
  const removed = new Set(update.removedIds || []);
  const rounds = snapshot.rounds
    .filter((r) => !removed.has(r.id))
    .map((r) => {
      const patch = publicRows.get(r.id);
      publicRows.delete(r.id);
      return patch ? merge(r, patch) : r;
    });
  rounds.push(...publicRows.values());
  const changes = new Map(update.rounds.map((r) => [r.id, r]));
  return {
    ...snapshot,
    sequence: update.sequence,
    serverTime: update.serverTime,
    rounds: rounds.sort((a, b) => b.start - a.start).slice(0, 50),
    // Leaving the latest-50 market window must never delete a user's past prediction.
    predictions: snapshot.predictions.map((r) => {
      const patch = changes.get(r.id);
      return patch ? merge(r, patch) : r;
    }),
    marketStatus: update.marketStatus,
    marketUpdatedAt: update.marketUpdatedAt,
  };
}
export function isUsdReady(round: Round, now: number): boolean {
  return (
    round.canonical !== false &&
    round.usdStatus === 'ready' &&
    (round.usdValidUntil || 0) > now &&
    Number(round.currentPriceUsd) > 0 &&
    Number(round.currentMCUsd) > 0
  );
}
export function visibleRounds(rounds: Round[], now: number): Round[] {
  return rounds.filter((r) => isUsdReady(r, now));
}
